import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Broadcast, ArrowRight, MusicNote, Play, Star } from "@phosphor-icons/react";
import { fetchCategories, fetchTracks, fetchArticles, fetchActiveLive } from "../lib/api";
import TrackCard from "../components/TrackCard";

const HERO_BG =
  "https://images.unsplash.com/photo-1529753253655-470be9a42781?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1800";

const CATEGORY_ICONS = {
  nohay: "☽",
  "majalis-lectures": "◈",
  manqabat: "✦",
  marsiya: "⸻",
  "holy-quran": "◉",
  "dua-supplication": "❋",
};

const ANIM = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

export default function Home() {
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [latestAudio, setLatestAudio] = useState([]);
  const [latestVideo, setLatestVideo] = useState([]);
  const [articles, setArticles] = useState([]);
  const [live, setLive] = useState(null);

  useEffect(() => {
    fetchCategories().then(setCats).catch(() => {});
    fetchTracks({ featured: true, limit: 6 }).then(setFeatured).catch(() => {});
    fetchTracks({ type: "audio", limit: 8 }).then(setLatestAudio).catch(() => {});
    fetchTracks({ type: "video", limit: 6 }).then(setLatestVideo).catch(() => {});
    fetchArticles({ limit: 3 }).then(setArticles).catch(() => {});
    fetchActiveLive().then(setLive).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="bg-[#08090C]">

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden min-h-[88vh] flex items-end border-b border-white/5">
        {/* Background layers */}
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#08090C]/20 via-[#08090C]/60 to-[#08090C]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#08090C]/70 via-transparent to-transparent" />
        </div>

        {/* Islamic geometric pattern overlay */}
        <div className="absolute inset-0 islamic-pattern opacity-100 pointer-events-none" />

        {/* Top-right decorative crescent / calligraphy */}
        <div
          className="absolute top-12 right-10 hidden lg:block font-arabic text-[#B79C7B]/12 select-none pointer-events-none"
          style={{ fontSize: "9rem", lineHeight: 1 }}
          aria-hidden
        >
          بِسْمِ اللّٰه
        </div>

        <div className="relative w-full max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 pt-40 grid lg:grid-cols-12 gap-10 items-end">
          <motion.div
            initial="hidden"
            animate="show"
            variants={ANIM}
            className="lg:col-span-7"
          >
            {/* Label */}
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-8 bg-[#B79C7B]/60" />
              <span className="overline text-[#B79C7B]">Muharram Archive · ShiaTunes</span>
            </div>

            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[0.92] tracking-tight text-ivory text-balance">
              A sacred space for the
              <br />
              <span className="italic text-[#C4A882]">voices of Karbala.</span>
            </h1>

            <p className="mt-8 max-w-xl text-base lg:text-lg text-[#9CA3AF] leading-relaxed">
              Stream nohay, majalis, manqabat and marsiya from the most
              cherished reciters — and join live broadcasts of nightly
              programs from around the world.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/live"
                className="inline-flex items-center gap-3 h-12 px-7 text-sm tracking-wider text-ivory transition-all hover:brightness-110 active:scale-95"
                style={{
                  background: "linear-gradient(135deg, #A33B3B 0%, #8A2E2E 60%, #6B2020 100%)",
                  boxShadow: "0 0 28px rgba(138,46,46,0.45)",
                }}
                data-testid="hero-live-button"
              >
                <Broadcast size={18} weight="duotone" />
                {live?.is_live ? "Join Live Now" : "Live Schedule"}
              </Link>
              <Link
                to="/category/nohay"
                className="inline-flex items-center gap-3 h-12 px-7 border border-[#B79C7B]/30 hover:border-[#B79C7B] hover:text-[#C4A882] transition-colors text-sm tracking-wider"
                data-testid="hero-browse-button"
              >
                <MusicNote size={16} weight="duotone" />
                Browse Nohay Archive
              </Link>
            </div>

            {/* Stats row */}
            <div className="mt-12 flex items-center gap-8 text-sm">
              {[
                { n: "500+", label: "Nohay" },
                { n: "120+", label: "Reciters" },
                { n: "24/7", label: "Live Streams" },
              ].map((s) => (
                <div key={s.label} className="flex flex-col">
                  <span className="font-serif text-2xl text-[#C4A882]">{s.n}</span>
                  <span className="overline text-[#71717A] text-[0.6rem]">{s.label}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Live card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div
              className="p-6 border border-white/8"
              style={{ background: "rgba(12,12,16,0.82)", backdropFilter: "blur(24px)" }}
            >
              {/* Live indicator */}
              <div className="flex items-center gap-2 mb-4">
                <span
                  className={`inline-block w-2 h-2 rounded-full bg-[#FF3B30] ${
                    live?.is_live ? "animate-pulseLive" : "opacity-40"
                  }`}
                />
                <span className="overline text-[#FF3B30] text-[0.65rem]">
                  {live?.is_live ? "Now Live" : "Next Broadcast"}
                </span>
              </div>

              <h3 className="font-serif text-2xl lg:text-3xl text-ivory leading-tight">
                {live?.title || "Majlis-e-Aza — Live from the Center"}
              </h3>
              <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed line-clamp-3">
                {live?.description ||
                  "Nightly majlis broadcast with leading speakers and reciters from the community."}
              </p>

              <div className="mt-5 h-px bg-gradient-to-r from-[#B79C7B]/30 to-transparent" />

              <Link
                to="/live"
                className="mt-5 inline-flex items-center gap-2 text-sm text-[#B79C7B] hover:text-ivory transition-colors group"
                data-testid="hero-live-tile-link"
              >
                Open broadcast room
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CATEGORIES ───────────────────────────────────────── */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="h-px w-6 bg-[#B79C7B]/50" />
              <span className="overline text-[#B79C7B]">Browse by Section</span>
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl text-ivory">The Archive</h2>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {cats.map((c, i) => (
            <motion.div
              key={c.id}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
              variants={ANIM}
            >
              <Link
                to={`/category/${c.slug}`}
                className="group relative flex flex-col aspect-[3/4] overflow-hidden border border-[#1E1E22] hover:border-[#B79C7B]/50 transition-all duration-500"
                data-testid={`category-tile-${c.slug}`}
              >
                {c.thumbnail && (
                  <img
                    src={c.thumbnail}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-65 group-hover:scale-110 transition-all duration-700"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/95" />

                {/* Corner ornament */}
                <div className="absolute top-2 right-2 text-[#B79C7B]/40 text-lg leading-none select-none" aria-hidden>
                  {CATEGORY_ICONS[c.slug] || "◆"}
                </div>

                <div className="relative mt-auto p-4">
                  <div className="overline text-[#B79C7B] mb-1 text-[0.6rem]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-serif text-lg lg:text-xl text-ivory leading-tight group-hover:text-[#D4B896] transition-colors">
                    {c.name}
                  </div>
                </div>

                {/* Hover bottom line */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#8A2E2E] to-[#B79C7B] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── FEATURED ─────────────────────────────────────────── */}
      {featured.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-6 bg-[#B79C7B]/50" />
            <span className="overline text-[#B79C7B]">Featured</span>
          </div>
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-serif text-3xl lg:text-4xl text-ivory">
              Editor&rsquo;s Selection
            </h2>
            <Star size={20} weight="duotone" className="text-[#B79C7B]/50" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {featured[0] && (
              <Link
                to={`/track/${featured[0].id}`}
                className="md:col-span-7 group relative aspect-video overflow-hidden border border-[#1E1E22] hover:border-[#B79C7B]/40 transition-colors"
                data-testid="featured-hero"
              >
                <img
                  src={featured[0].thumbnail}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/98 via-black/30 to-transparent" />

                {/* Play button */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all duration-300"
                  style={{ background: "rgba(138,46,46,0.9)", boxShadow: "0 0 32px rgba(138,46,46,0.6)" }}>
                  <Play size={24} weight="fill" className="text-ivory ml-1" />
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-7">
                  <div className="overline text-[#B79C7B] mb-2 text-[0.6rem]">
                    Featured · {featured[0].type}
                  </div>
                  <div className="font-serif text-3xl lg:text-4xl text-ivory leading-tight max-w-xl">
                    {featured[0].title}
                  </div>
                  <div className="text-sm text-[#9CA3AF] mt-2">{featured[0].reciter_name}</div>
                </div>
              </Link>
            )}

            <div className="md:col-span-5 flex flex-col gap-3">
              {featured.slice(1, 5).map((t) => (
                <Link
                  key={t.id}
                  to={`/track/${t.id}`}
                  className="group flex gap-4 border border-[#1E1E22] hover:border-[#B79C7B]/40 p-3 transition-colors"
                  data-testid={`featured-side-${t.id}`}
                >
                  <div className="w-20 h-20 bg-[#1A1A1D] shrink-0 overflow-hidden relative">
                    <img
                      src={t.thumbnail}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/50">
                      <Play size={16} weight="fill" className="text-ivory" />
                    </div>
                  </div>
                  <div className="min-w-0 py-1 flex flex-col justify-center">
                    <div className="overline text-[#71717A] text-[0.6rem]">{t.type}</div>
                    <div className="font-serif text-lg text-ivory leading-tight line-clamp-2 group-hover:text-[#D4B896] transition-colors mt-1">
                      {t.title}
                    </div>
                    <div className="text-xs text-[#9CA3AF] mt-1 truncate">{t.reciter_name}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── LATEST NOHAY / AUDIO ─────────────────────────────── */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        {/* Section header with decorative line */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="h-px w-6 bg-[#B79C7B]/50" />
              <span className="overline text-[#B79C7B]">Audio</span>
            </div>
            <h2 className="font-serif text-3xl text-ivory">Latest Recitations</h2>
          </div>
          <Link
            to="/category/nohay"
            className="text-sm text-[#71717A] hover:text-[#B79C7B] flex items-center gap-1.5 transition-colors group"
          >
            View all <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 lg:gap-5">
          {latestAudio.map((t, i) => (
            <motion.div
              key={t.id}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
              variants={ANIM}
            >
              <TrackCard track={t} queue={latestAudio} queueIndex={i} />
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── LATEST VIDEO ─────────────────────────────────────── */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 pb-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="h-px w-6 bg-[#B79C7B]/50" />
              <span className="overline text-[#B79C7B]">Video</span>
            </div>
            <h2 className="font-serif text-3xl text-ivory">Filmed Majalis</h2>
          </div>
          <Link
            to="/category/majalis-lectures"
            className="text-sm text-[#71717A] hover:text-[#B79C7B] flex items-center gap-1.5 transition-colors group"
          >
            View all <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {latestVideo.map((t, i) => (
            <motion.div
              key={t.id}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
              variants={ANIM}
            >
              <TrackCard track={t} queue={latestVideo} queueIndex={i} />
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── NEWS / ARTICLES ───────────────────────────────────── */}
      {articles.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 border-t border-white/5">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="h-px w-6 bg-[#B79C7B]/50" />
                <span className="overline text-[#B79C7B]">Editorial</span>
              </div>
              <h2 className="font-serif text-3xl text-ivory">News &amp; Reflections</h2>
            </div>
            <Link
              to="/news"
              className="text-sm text-[#71717A] hover:text-[#B79C7B] flex items-center gap-1.5 transition-colors group"
            >
              View all <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {articles.map((a, i) => (
              <motion.div
                key={a.id}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                custom={i}
                variants={ANIM}
              >
                <Link
                  to={`/news/${a.slug}`}
                  className="group border border-[#1E1E22] hover:border-[#B79C7B]/40 transition-colors block h-full"
                  data-testid={`article-card-${a.slug}`}
                >
                  <div className="aspect-[16/10] bg-[#111318] overflow-hidden">
                    {a.cover && (
                      <img
                        src={a.cover}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    )}
                  </div>
                  <div className="p-5">
                    <div className="overline text-[#71717A] mb-2 text-[0.6rem]">
                      {new Date(a.published_at).toLocaleDateString(undefined, {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </div>
                    <div className="font-serif text-xl text-ivory leading-tight group-hover:text-[#D4B896] transition-colors">
                      {a.title}
                    </div>
                    <p className="text-sm text-[#9CA3AF] mt-2.5 line-clamp-3 leading-relaxed">
                      {a.excerpt}
                    </p>
                    <div className="mt-4 flex items-center gap-1.5 text-xs text-[#B79C7B] opacity-0 group-hover:opacity-100 transition-opacity">
                      Read more <ArrowRight size={12} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ── FOOTER TAGLINE ────────────────────────────────────── */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 text-center border-t border-white/5">
        <p
          className="font-arabic text-[#B79C7B]/30 select-none text-5xl"
          aria-hidden
        >
          يا حسين
        </p>
        <p className="mt-4 text-xs text-[#4A4A4E] overline tracking-widest">
          Every recitation, a salaam.
        </p>
      </div>
    </div>
  );
}
